// @flow
import React, { useContext, useRef } from "react";
import classnames from "classnames";
import { Loader, Dimmer } from "semantic-ui-react";
import { Helmet } from "react-helmet";

import { read, readAndReplace } from "modules/ChangeServiceForm/misc/utilities";
import ChangeServiceFormSharedContext from "../../../misc/context";

import MultiStepper from "../../shared/MultiStepper/MultiStepper";
import { Row, Label } from "../../shared/CommonComponents";
import ProgressBar from "../../shared/ProgressBar/ProgressBar";

import PropertyType from "../../fields/PropertyType";
import SwitchProviderOrMove from "../../fields/SwitchProviderOrMove";
import DesiredDate from "../../fields/DesiredDate/DesiredDate";
import PropertyTypeChanges from "../../fields/Changes/PropertyTypeChanges";
import DesiredDateChanges from "../../fields/Changes/DesiredDateChanges";
import ConsumptionHandler from "../../fields/Consumption/ConsumptionHandler";
import ProvidersAndTariffsHandler from "../../fields/ProviderAndTariff/ProviderAndTariffHandler";
import EcoTariff from "../../fields/EcoTariff";
import HasCustomerData from "../../fields/CustomerData/HasCustomerData";
import Contact from "../../fields/Contact/ContactMobile";
import BillingAddressMobile from "../../fields/BillingAddressMobile";
import BankDetails from "../../fields/BankDetails/BankDetails";
import AccountOwnerMobile from "../../fields/AccountOwnerMobile";
import FormOverview from "../../shared/FormOverview/FormOverview";
import InfoBox from "../../shared/InfoBox";

const ChangeServiceForm_initial = () => {
  const {
    category: { categoryType },
    loginOrRegisterStep: { showLoginOrRegister },
    configurationData: {
      formData,
      stepsDependencies,
      translations: { fieldLabels, glossary, savingsHeader },
      inlineStyles,
    },
    inheritedStyles,
    steps: { step, totalSteps, stepName },
    loading,
  } = useContext(ChangeServiceFormSharedContext);

  // Data retrieved from the json file
  const progessBarIcon = formData?.["COMMON"].progessBarIcon || null;
  const propertyTypeData = formData?.["COMMON"].propertyType || null;
  const consumptionData = categoryType ? formData?.[categoryType].consumption : null;
  const ecoTariffData = formData?.["COMMON"].ecoTariff || null;
  const switchProviderOrMoveData = formData?.["COMMON"].switchProviderOrMove || null;
  const customerData = formData?.["COMMON"].customerData || null;
  const cancelOldContractOpts = formData?.["COMMON"].cancelOldContract || null;

  const isMultiStepperFirstStep = step === 1;

  const {
    form,
    wide,
    formHeader,
    formBody,
    formFooter,
    formRow,
    formLabel,
    savings: savingsStyles,
    savingsTotal,
    popup,
    tooltip,
    infoBoxContainer,
    labelText,
    withInfoBox,
    formTransitionWrapperCS,
  } = inheritedStyles;

  const infoBoxClasses = { popup, tooltip, infoBoxContainer, labelText, withInfoBox };

  /* Ref for the transitioning effect */
  const formTransitionWrapperCSref: any = useRef();
  let savings = formData?.COMMON?.savings;

  return (
    <>
      <Helmet>
        <script src="https://kit.fontawesome.com/ab27ad7273.js" crossOrigin="anonymous"></script>
      </Helmet>
      <div className={classnames(form, { [wide]: showLoginOrRegister })}>
        <div
          className={classnames(formTransitionWrapperCS, { ["LoginOrRegisterStep"]: showLoginOrRegister })}
          ref={formTransitionWrapperCSref}
        >
          {savings && (
            <div className={classnames(savingsStyles, "theme-color-primary")}>
              {read(savingsHeader.mainText)} <span className={savingsTotal}>{savings}</span>
            </div>
          )}
          <div className={formHeader}>
            <ProgressBar iconWebReference={progessBarIcon} stepsCount={totalSteps} actualStep={step} />
          </div>
          <div className={formBody}>
            {!loading ? (
              <MultiStepper transitionWrapper={formTransitionWrapperCSref}>
                {/* STEP 1 */}
                <MultiStepper.Step stepName="CHANGEREASON">
                  <Row className={formRow}>
                    <Label
                      className={formLabel}
                      infoBoxText={read(fieldLabels.switchProviderOrMove.tooltip)}
                      infoBoxClasses={infoBoxClasses}
                    >
                      {read(fieldLabels.switchProviderOrMove.label)}
                    </Label>
                    <SwitchProviderOrMove
                      switchProviderOrMoveData={switchProviderOrMoveData}
                      inheritedStyles={inheritedStyles}
                      inlineSty3les={inlineStyles}
                      stepName={stepName}
                    />
                  </Row>
                </MultiStepper.Step>

                {/* STEP 2 */}
                <MultiStepper.Step stepName="OBJECT">
                  <Row className={formRow}>
                    <Label
                      className={formLabel}
                      infoBoxText={read(fieldLabels.propertyType.tooltip)}
                      infoBoxClasses={infoBoxClasses}
                    >
                      {read(fieldLabels.propertyType.label)}
                    </Label>
                    <PropertyType
                      propertyTypeData={propertyTypeData}
                      inheritedStyles={inheritedStyles}
                      inlineStyles={inlineStyles}
                      stepName={stepName}
                    />
                    <PropertyTypeChanges consumptionData={consumptionData} />
                  </Row>
                </MultiStepper.Step>

                {/* STEP 3 */}
                <MultiStepper.Step stepName="VERBRAUCH">
                  <Row className={classnames(formRow)}>
                    <Label
                      className={formLabel}
                      infoBoxText={read(fieldLabels.expectedConsumption.tooltip)}
                      infoBoxClasses={infoBoxClasses}
                    >
                      {read(fieldLabels.expectedConsumption.label)}
                    </Label>
                    <ConsumptionHandler consumptionData={consumptionData} />
                  </Row>
                </MultiStepper.Step>

                {/* STEP 4 */}
                <MultiStepper.Step stepName="TARIFF_PROVIDER">
                  <Row className={classnames(formRow)}>
                    <Label
                      className={formLabel}
                      infoBoxText={read(fieldLabels.providerAndTariff.tooltip)}
                      infoBoxClasses={infoBoxClasses}
                    >
                      {read(fieldLabels.providerAndTariff.label)}
                    </Label>
                    <ProvidersAndTariffsHandler />
                  </Row>
                  <Row className={formRow}>
                    <Label
                      className={formLabel}
                      infoBoxText={read(fieldLabels.desiredGreenPower.tooltip)}
                      infoBoxClasses={infoBoxClasses}
                    >
                      {read(fieldLabels.desiredGreenPower.label)}
                    </Label>
                    <EcoTariff
                      categoryType={categoryType || ""}
                      ecoTariffData={ecoTariffData}
                      inheritedStyles={inheritedStyles}
                      stepName={stepName}
                    />
                  </Row>
                </MultiStepper.Step>

                {/* STEP 5 */}
                <MultiStepper.Step stepName="CANCELLATION">
                  <Row className={classnames(formRow)}>
                    <Label
                      className={formLabel}
                      infoBoxText={read(fieldLabels.cancelOldContract.tooltip)}
                      infoBoxClasses={infoBoxClasses}
                    >
                      {read(fieldLabels.cancelOldContract.label)}
                    </Label>
                    <DesiredDate
                      cancelOldContractOpts={cancelOldContractOpts}
                      inheritedStyles={inheritedStyles}
                      inlineStyles={inlineStyles}
                      stepName={stepName}
                    />
                  </Row>
                  <DesiredDateChanges consumptionData={consumptionData} />
                </MultiStepper.Step>

                {/* STEP 6 */}
                <MultiStepper.Step stepName="DELIVERYADDRESS">
                  <Row className={formRow}>
                    <Label
                      className={formLabel}
                      infoBoxText={readAndReplace(fieldLabels.contact.tooltip.main, {
                        replace: (domNode) => {
                          if (domNode.attribs && domNode.attribs.id === "replace") {
                            return <span>{categoryType && glossary[categoryType.toLowerCase()].trans}</span>;
                          }
                        },
                      })}
                      infoBoxClasses={infoBoxClasses}
                    >
                      {readAndReplace(fieldLabels.contact.label, {
                        replace: (domNode) => {
                          if (domNode.attribs && domNode.attribs.id === "replace") {
                            return <span>{categoryType && glossary[categoryType.toLowerCase()].trans}</span>;
                          }
                        },
                      })}
                    </Label>
                    <Contact typeOfContact="main" inheritedStyles={inheritedStyles} />
                  </Row>
                </MultiStepper.Step>

                {/* STEP 7 */}
                <MultiStepper.Step stepName="BILLINGADDRESS">
                  <Row className={formRow}>
                    <Label
                      className={formLabel}
                      infoBoxText={read(fieldLabels.billingAddress.tooltip)}
                      infoBoxClasses={infoBoxClasses}
                    >
                      {read(fieldLabels.billingAddress.label)}
                    </Label>
                    <BillingAddressMobile
                      content={fieldLabels.billingAddress}
                      inheritedStyles={inheritedStyles}
                      stepName={stepName}
                      categoryType={categoryType}
                      glossary={glossary}
                    />
                  </Row>
                </MultiStepper.Step>

                {/* STEP 8 */}
                <MultiStepper.Step stepName="BANKDATA">
                  <InfoBox
                    content={fieldLabels.billingAddress}
                    inheritedStyles={inheritedStyles}
                    categoryType={categoryType}
                    glossary={glossary}
                  />
                  <Row className={formRow}>
                    <Label
                      className={formLabel}
                      infoBoxText={read(fieldLabels.bankDetails.tooltip)}
                      infoBoxClasses={infoBoxClasses}
                    >
                      {read(fieldLabels.bankDetails.label)}
                    </Label>
                    <BankDetails inheritedStyles={inheritedStyles} fieldLabels={fieldLabels} stepName={stepName} />
                  </Row>
                </MultiStepper.Step>

                {/* STEP 9 */}
                <MultiStepper.Step stepName="CARDHOLDER">
                  <Row className={formRow}>
                    <Label
                      className={formLabel}
                      infoBoxText={read(fieldLabels.accountHolder.tooltip)}
                      infoBoxClasses={infoBoxClasses}
                    >
                      {read(fieldLabels.accountHolder.label)}
                    </Label>
                    <AccountOwnerMobile
                      content={fieldLabels.accountHolder}
                      inheritedStyles={inheritedStyles}
                      stepName={stepName}
                      categoryType={categoryType}
                      glossary={glossary}
                    />
                  </Row>
                </MultiStepper.Step>

                {/* STEP 10 */}
                <MultiStepper.Step stepName="CUSTOMERDATA">
                  <InfoBox
                    content={fieldLabels.hasCustomerData}
                    inheritedStyles={inheritedStyles}
                    categoryType={categoryType}
                    glossary={glossary}
                  />
                  <Row className={classnames(formRow)}>
                    <Label
                      className={formLabel}
                      infoBoxText={read(fieldLabels.hasCustomerData.tooltip)}
                      infoBoxClasses={infoBoxClasses}
                    >
                      {read(fieldLabels.hasCustomerData.label)}
                    </Label>
                    <HasCustomerData
                      data={customerData}
                      inheritedStyles={inheritedStyles}
                      inlineStyles={inlineStyles}
                      stepName={stepName}
                    />
                  </Row>
                </MultiStepper.Step>

                {/* STEP 11 */}
                <MultiStepper.Step stepName="OVERVIEW">
                  <Row className={classnames(formRow)}>
                    <Label className={formLabel}>{read(fieldLabels.overview)}</Label>
                    <FormOverview
                      switchProviderOrMoveData={switchProviderOrMoveData}
                      propertyTypeData={propertyTypeData}
                    />
                  </Row>
                </MultiStepper.Step>
              </MultiStepper>
            ) : (
              <div style={{ height: "420px" }}>
                <Dimmer active inverted>
                  <Loader inverted content="Loading" data-testid="loader" />
                </Dimmer>
              </div>
            )}
          </div>
          {isMultiStepperFirstStep && (
            <div className={formFooter}>
              <span>{stepsDependencies?.[step - 1]?.footerText}</span>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ChangeServiceForm_initial;
